import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { CloudOff, SearchX } from 'lucide-react'
import { buttonVariants, Button } from '@/components/ui/button'
import { IconBadge } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'

/* Spec PARTNER-001 — estados de sistema (carregando, nao encontrado, falha ao carregar). */

function StatusLayout({ icon, title, body, action }: { icon: ReactNode; title: string; body: string; action?: ReactNode }) {
    return (
        <div className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-6 py-10 text-center">
            <IconBadge className="size-16">{icon}</IconBadge>
            <h1 className="m-0 font-display text-2xl font-bold">{title}</h1>
            <p className="m-0 max-w-xs text-[0.95rem] leading-relaxed text-ink-muted">{body}</p>
            {action}
        </div>
    )
}

export function SplashScreen() {
    const { t } = useTranslation()
    return (
        <div role="status" className="flex min-h-dvh items-center justify-center bg-app-background text-brand">
            <Spinner className="size-9" />
            <span className="sr-only">{t('common.loading')}</span>
        </div>
    )
}

export function NotFoundScreen() {
    const { t } = useTranslation()
    return (
        <StatusLayout
            icon={<SearchX className="size-7" aria-hidden="true" />}
            title={t('shell.notFound.title')}
            body={t('shell.notFound.body')}
            action={<Link to="/" className={buttonVariants({ variant: 'primary', size: 'md' })}>{t('shell.notFound.action')}</Link>}
        />
    )
}

export function NetworkErrorScreen({ onRetry }: { onRetry: () => void }) {
    const { t } = useTranslation()
    return (
        <main className="min-h-dvh bg-app-background">
            <StatusLayout
                icon={<CloudOff className="size-7" aria-hidden="true" />}
                title={t('shell.offline.title')}
                body={t('shell.offline.body')}
                action={<Button variant="primary" size="md" onClick={onRetry}>{t('common.retry')}</Button>}
            />
        </main>
    )
}

/* Bloco de carregando / falha dentro de uma tela ja montada. */
export function LoadingBlock() {
    const { t } = useTranslation()
    return (
        <div role="status" className="flex justify-center py-12 text-brand">
            <Spinner className="size-8" />
            <span className="sr-only">{t('common.loading')}</span>
        </div>
    )
}

export function LoadErrorBlock({ message, onRetry }: { message?: string; onRetry: () => void }) {
    const { t } = useTranslation()
    return (
        <div role="alert" className="flex flex-col items-center gap-3 rounded-card bg-subtle px-5 py-8 text-center">
            <CloudOff className="size-7 text-ink-muted" aria-hidden="true" />
            <p className="m-0 text-[0.95rem] font-semibold">{message ?? t('shell.loadError')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>{t('common.retry')}</Button>
        </div>
    )
}
